import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';

// Custom API Error class for better error handling
class ApiErrorClass extends Error {
  public response?: {
    status?: number;
    statusText?: string;
    data?: any;
  };

  constructor(message: string, response?: any) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiErrorClass);
    }
  }
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://orderapp-backend.ssemrah33.workers.dev/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    });
    
    if (error.response?.data?.error) {
      const customError = new ApiErrorClass(error.response.data.error, error.response);
      return Promise.reject(customError);
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API response handler
const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  console.log('API Response:', {
    status: response.status,
    data: response.data,
    url: response.config?.url,
  });
  
  if (response.data.error) {
    throw new Error(response.data.error);
  }
  
  if (response.data.data) {
    return response.data.data as T;
  }
  
  console.warn('API response missing data field, returning full response:', response.data);
  return response.data as T;
};

// Generic API methods
export const apiService = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.get<ApiResponse<T>>(url, config);
    return handleApiResponse(response);
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config);
    return handleApiResponse(response);
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data, config);
    return handleApiResponse(response);
  },

  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
    return handleApiResponse(response);
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.delete<ApiResponse<T>>(url, config);
    return handleApiResponse(response);
  },
};

export default apiClient; 